import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  MenuItem,
  Box,
  Alert,
  Typography,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const NEW_VENDOR_PREFIX = '__create__:';

// Loose name comparison so e.g. "Meko" matches an existing "Meko Finland".
function normalizeVendorName(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9äöå]+/gi, '');
}

function findVendorMatch(hint, vendors) {
  const h = normalizeVendorName(hint);
  if (!h) return null;
  let best = null;
  let bestScore = 0;
  for (const v of vendors) {
    const n = normalizeVendorName(v.name);
    if (!n) continue;
    let score = 0;
    if (n === h) score = 100;
    else if (n.startsWith(h) || h.startsWith(n)) score = 85;
    else if (n.includes(h) || h.includes(n)) score = 70;
    if (score > bestScore) {
      bestScore = score;
      best = v;
    }
  }
  return bestScore >= 70 ? best : null;
}

/**
 * Create-catalogue dialog.
 *
 * Flow optimized for the common case: pick the file format first; the vendor is
 * then auto-matched to an existing one (fuzzy) or pre-filled as a new vendor,
 * and the catalogue name is auto-generated ("<vendor> hinnasto" + running
 * number on collision). Everything stays editable. The same canonical Vendor is
 * reused for the catalogue and every Part materialized from it — never
 * duplicated for a supplier the workshop already has.
 */
function CreateCatalogueDialog({ open, handleClose, profiles, existingNames = [], onCreated }) {
  const { t } = useTranslation();
  const [profileKey, setProfileKey] = useState('');
  const [vendorOptions, setVendorOptions] = useState([]);
  const [vendorValue, setVendorValue] = useState(null);
  const [vendorInput, setVendorInput] = useState('');
  const [vendorAutoMatched, setVendorAutoMatched] = useState(false);
  const [vendorTouched, setVendorTouched] = useState(false);
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const suffix = t('catalogues.autoNameSuffix');

  const autoName = useCallback(
    (vendorName) => {
      const base = `${vendorName} ${suffix}`.trim();
      if (!existingNames.includes(base)) return base;
      let n = 2;
      while (existingNames.includes(`${base} ${n}`)) n += 1;
      return `${base} ${n}`;
    },
    [suffix, existingNames]
  );

  useEffect(() => {
    if (!open) return;
    setProfileKey('');
    setVendorValue(null);
    setVendorInput('');
    setVendorAutoMatched(false);
    setVendorTouched(false);
    setName('');
    setNameTouched(false);
    setError(null);
    setVendorsLoading(true);
    axios
      .get('/api/vendors/', { params: { typeahead: 'true' } })
      .then((res) => setVendorOptions(Array.isArray(res.data) ? res.data : res.data.results || []))
      .catch(() => setVendorOptions([]))
      .finally(() => setVendorsLoading(false));
  }, [open]);

  // Auto-fill vendor + name from the chosen format (unless the user overrode them).
  const applyFormatDefaults = useCallback(
    (key, vendors) => {
      const profile = (profiles || []).find((p) => p.key === key);
      const hint = profile?.vendor_hint;
      let vendor = vendorValue;
      if (!vendorTouched) {
        if (hint) {
          const match = findVendorMatch(hint, vendors);
          if (match) {
            vendor = match;
            setVendorAutoMatched(true);
          } else {
            vendor = { id: `${NEW_VENDOR_PREFIX}${hint}`, name: hint, isNew: true };
            setVendorAutoMatched(false);
          }
        } else {
          vendor = null;
          setVendorAutoMatched(false);
        }
        setVendorValue(vendor);
        setVendorInput(vendor ? vendor.name : '');
      }
      if (!nameTouched) {
        setName(vendor ? autoName(vendor.name) : '');
      }
    },
    [profiles, vendorValue, vendorTouched, nameTouched, autoName]
  );

  const handleFormatChange = (key) => {
    setProfileKey(key);
    applyFormatDefaults(key, vendorOptions);
  };

  // If vendors arrive after a format was already chosen, run the match then.
  useEffect(() => {
    if (profileKey && !vendorsLoading && !vendorTouched) {
      applyFormatDefaults(profileKey, vendorOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorsLoading]);

  const handleVendorChange = (value) => {
    setVendorValue(value);
    setVendorTouched(true);
    setVendorAutoMatched(false);
    if (!nameTouched) {
      setName(value ? autoName(value.name) : '');
    }
  };

  const filterVendorOptions = (options, state) => {
    const input = state.inputValue.trim().toLowerCase();
    const filtered = options.filter((o) => o.name.toLowerCase().includes(input));
    if (input && !options.some((o) => o.name.toLowerCase() === input)) {
      filtered.push({
        id: `${NEW_VENDOR_PREFIX}${state.inputValue.trim()}`,
        name: state.inputValue.trim(),
        isNew: true,
      });
    }
    return filtered;
  };

  const handleSubmit = async () => {
    setError(null);
    if (!name.trim() || !profileKey || !vendorValue) {
      setError(t('catalogues.createValidationError'));
      return;
    }
    setSubmitting(true);
    try {
      const payload = { name: name.trim(), vendor_profile: profileKey };
      if (vendorValue.isNew) {
        payload.new_vendor = { name: vendorValue.name };
      } else {
        payload.vendor_id = vendorValue.id;
      }
      const response = await axios.post('/api/vendor-catalogues/', payload);
      if (onCreated) onCreated(response.data);
      handleClose();
    } catch (err) {
      setError(t('catalogues.createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('catalogues.newCatalogue')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          {/* 1. File format first — usually the only field that needs a choice. */}
          <TextField
            select
            label={t('catalogues.profile')}
            value={profileKey}
            onChange={(e) => handleFormatChange(e.target.value)}
            helperText={t('catalogues.profileHelp')}
            fullWidth
            autoFocus
            data-testid="catalogue-profile-select"
          >
            {(profiles || []).map((profile) => (
              <MenuItem key={profile.key} value={profile.key}>
                {profile.display_name}
              </MenuItem>
            ))}
          </TextField>

          {/* Vendor + name appear only once a format is chosen — they're
              auto-filled from it, so usually nothing else needs touching. */}
          {profileKey && (
          <>
          {/* 2. Vendor — auto-matched / pre-filled from the format. */}
          <Box>
            <Autocomplete
              options={vendorOptions}
              value={vendorValue}
              onChange={(e, value) => handleVendorChange(value)}
              inputValue={vendorInput}
              onInputChange={(e, value) => setVendorInput(value)}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={filterVendorOptions}
              loading={vendorsLoading}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.isNew ? (
                    <Typography sx={{ fontStyle: 'italic' }}>
                      {t('catalogues.createVendorInline', { name: option.name })}
                    </Typography>
                  ) : (
                    option.name
                  )}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('catalogues.vendor')}
                  helperText={t('catalogues.vendorHelp')}
                  inputProps={{ ...params.inputProps, 'data-testid': 'catalogue-vendor-input' }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {vendorsLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {vendorValue && vendorAutoMatched && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, color: 'success.main' }}>
                <CheckCircleIcon sx={{ fontSize: 16 }} />
                <Typography variant="body2" data-testid="vendor-automatched-note">
                  {t('catalogues.vendorAutoMatched', { name: vendorValue.name })}
                </Typography>
              </Box>
            )}
            {vendorValue && vendorValue.isNew && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, color: 'info.main' }}>
                <AddCircleIcon sx={{ fontSize: 16 }} />
                <Typography variant="body2" data-testid="vendor-willcreate-note">
                  {t('catalogues.vendorWillCreate', { name: vendorValue.name })}
                </Typography>
              </Box>
            )}
          </Box>

          {/* 3. Name — auto-generated, editable. */}
          <TextField
            label={t('catalogues.name')}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameTouched(true);
            }}
            fullWidth
            inputProps={{ 'data-testid': 'catalogue-name-input' }}
          />
          </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>{t('Cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !profileKey}
          data-testid="catalogue-create-submit"
        >
          {submitting ? <CircularProgress size={20} /> : t('catalogues.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreateCatalogueDialog;
